import React, { useState } from 'react';
import { Tag } from 'antd';
import { Link } from 'react-router-dom';
import { Project } from 'mdello-client-ts/dist/src/interfaces/common/project.interface';
import { isCurrentProjectStimull } from 'src/util/projectCheckers';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { useQueryParams } from 'src/hooks/useQueryParams';
import { useGetOrdersGroup } from 'src/reactQuery/orders/useGetOrderGroup';
import { OrderGroupDetailed } from 'src/reactQuery/orders/interfaces/orderGroup.interface';

import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import Loader from '../../component/Loader/Loader';
import Copy from '../../component/Copy';

interface OrderGroupMapped {
  buyer: string;
  id: string;
  items: number;
  key: number;
  location: string;
  sellers: number;
  status: string;
  total: number;
}

const OrdersGroup = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectedFilter, setSelectedFilter] = useState('All');

  const currentProject = getDataFromLocalStorage<Project | undefined>(
    LocalStorageKey.CURRENT_PROJECT
  );
  const { queryParams } = useQueryParams<{
    page?: string;
    pageSize?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }>();
  const { data: ordersGroupData, isLoading } = useGetOrdersGroup({
    page: queryParams.page,
    pageSize: queryParams.pageSize,
    search: queryParams.search,
    sortBy: queryParams.sortBy,
    sortOrder: queryParams.sortOrder
  });

  const onDownloadCsv = () => {
    if (isCurrentProjectStimull) {
      // add function here
    }
    // dispatch(downloadFileAction());
  };

  const mapToTable = (orders: OrderGroupDetailed[]): OrderGroupMapped[] => {
    const data: OrderGroupMapped[] = [];

    if (!orders || orders.length === 0) {
      return data;
    }

    orders.forEach(({ _id, orders, totalPayment, customerProfile, status }, index) => {
      const itemsArr = orders.map((order) => {
        return order.selectedItems.length;
      });
      const items = itemsArr.reduce((prevValue, currentValue) => {
        return prevValue + currentValue;
      }, 0);
      const sellersArr = orders.map((order) => {
        return order.sellerProfile.userId;
      });
      const sellers = new Set(sellersArr);
      const deliveryLocation = orders[0].dropoffAddress?.street || '';
      data.push({
        buyer: customerProfile?.userName,
        id: _id,
        items,
        key: index,
        location: deliveryLocation,
        sellers: sellers.size,
        status,
        total: Number(totalPayment.toFixed(2))
      });
    });

    return data;
  };

  const filterData = (data: OrderGroupMapped[]) => {
    if (!data || !data.length) {
      return;
    }

    if (selectedFilter !== 'All') {
      return data.filter((order) => order.status === selectedFilter);
    }

    return data;
  };

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => {
        return <Copy text={id} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) => a.id.localeCompare(b.id),
      title: 'Order ID'
    },
    {
      dataIndex: 'buyer',
      key: 'buyer',
      render: (buyer: string) => {
        return <Copy text={buyer} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) =>
        a.buyer.toString().localeCompare(b.buyer),
      title: 'Buyer'
    },
    {
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => {
        return <Copy text={location} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) =>
        a.location.toString().localeCompare(b.location),
      title: 'Delivery Location'
    },
    {
      dataIndex: 'sellers',
      key: 'sellers',
      render: (sellers: number) => {
        return <Copy text={sellers} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) =>
        a.sellers.toString().localeCompare(b.sellers.toString()),
      title: 'Seller(s)'
    },
    {
      dataIndex: 'items',
      key: 'items',
      render: (items: number) => {
        return <Copy text={items} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) =>
        a.items.toString().localeCompare(b.items.toString()),
      title: 'Item(s)'
    },
    {
      dataIndex: 'total',
      key: 'total',
      render: (total: string) => {
        return <Copy text={total} />;
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) => a.total - b.total,
      title: 'total'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let style = {};
        switch (status) {
          case 'delivered':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '90%'
            };
            break;
          case 'pending':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '90%'
            };
            break;
          case 'cancelled':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '90%'
            };
            break;
          case 'accepted':
            style = {
              backgroundColor: 'rgba(244, 211, 37, 0.15)',
              border: 'none',
              color: '#F4CD00',
              width: '90%'
            };
            break;
          default:
            style = {
              width: '90%'
            };
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a: OrderGroupMapped, b: OrderGroupMapped) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      key: 'edit',
      render: (order: OrderGroupMapped) => {
        return (
          <Tag
            style={{
              overflow: 'hidden',
              padding: '0'
            }}
          >
            <Link
              style={{
                alignItems: 'center',
                color: 'inherit',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                transition: 'none',
                width: '100%'
              }}
              to={`/ecommerce/orders-group/${order.id}`}
            >
              View
            </Link>
          </Tag>
        );
      }
    }
  ];

  const actions = [
    {
      onClick: onDownloadCsv,
      title: 'Download .CSV',
      type: 'primary'
    }
  ];

  const filterList = [
    'All',
    'accepted',
    'processing',
    'onHold',
    'cancelled',
    'delivering',
    'delivered',
    'completed',
    'refunded',
    'failed',
    'trash'
  ];

  const onFilterChange = (value: string) => {
    setSelectedFilter(value);
  };
  const data = mapToTable(ordersGroupData?.data!);

  const filteredData = filterData(data);

  if (!currentProject || isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={ordersGroupData?.paging?.total} title="Orders Group" />
      <NoxTable
        actions={actions}
        columns={
          !isCurrentProjectStimull
            ? columns
            : columns.filter((column) => column.dataIndex !== 'location')
        }
        data={filteredData || []}
        filterList={[filterList]}
        onFilterChange={onFilterChange}
        searchBy={SearchBy.SELLER}
        selectedFilter={[selectedFilter]}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={ordersGroupData?.paging?.total}
      />
    </div>
  );
};

export default OrdersGroup;
